/* Vorschau der Player Card im Reiter Material (D4).
 *
 * Zeigt das obere Drittel der ersten Seite der ECHTEN, erzeugten Karte — dieselbe Funktion wie der Download
 * (useProfilePdf.cardBlobFor), kein zweiter Codepfad, dieselben Daten (buildProfile). Das PDF wird zu einem Bild
 * der ersten Seite gerendert (export/pdfPageImage.js) und oben zugeschnitten.
 *
 * Aufwand und schlechtes Netz:
 *  - erzeugt wird erst, wenn der Abschnitt sichtbar ist (start()), nicht beim Öffnen des Spielers;
 *  - Änderungen der Daten lösen erst nach `delay` ms Ruhe eine neue Erzeugung aus (nie je Tastendruck);
 *  - das Ergebnis einer überholten Erzeugung wird verworfen;
 *  - Ergebnisse werden je Datenstand zwischengespeichert (in dieser Instanz), Blob-URLs beim Verlassen freigegeben;
 *  - schlägt etwas fehl: Status 'failed', die Vorschau fällt weg — Download und Reiter bleiben unberührt.
 */

import { ref, watch } from 'vue';

export const PREVIEW_DELAY_MS = 600;
const CACHE_MAX = 6;

/**
 * @param {() => object} getPlayer
 * @param {{ keyOf: (player: object) => string,
 *           render: (player: object) => Promise<Blob>,
 *           delay?: number,
 *           makeUrl?: (blob: Blob) => string, revokeUrl?: (url: string) => void }} deps
 */
export function usePlayerCardPreview(getPlayer, { keyOf, render, delay = PREVIEW_DELAY_MS, makeUrl, revokeUrl }) {
  const mk = makeUrl || ((b) => URL.createObjectURL(b));
  const rv = revokeUrl || ((u) => URL.revokeObjectURL(u));

  const url = ref(null);
  /** 'idle' | 'loading' | 'ready' | 'failed' */
  const status = ref('idle');

  const cache = new Map(); // Datenstand -> Blob-URL
  let started = false;
  let disposed = false;
  let timer = null;
  let token = 0;
  let stopWatch = null;

  function remember(key, u) {
    cache.set(key, u);
    while (cache.size > CACHE_MAX) {
      const oldest = cache.keys().next().value;
      const old = cache.get(oldest);
      cache.delete(oldest);
      if (old !== url.value) rv(old);
    }
  }

  async function generate() {
    if (disposed) return;
    const player = getPlayer();
    const key = keyOf(player);
    if (cache.has(key)) {
      url.value = cache.get(key);
      status.value = 'ready';
      return;
    }
    const mine = ++token;
    status.value = 'loading';
    try {
      const image = await render(player);
      if (disposed || mine !== token) return; // überholt: Ergebnis verwerfen
      const u = mk(image);
      remember(key, u);
      url.value = u;
      status.value = 'ready';
    } catch (e) {
      if (disposed || mine !== token) return;
      console.error('[playerCardPreview] Vorschau nicht verfügbar:', e && e.message);
      url.value = null;
      status.value = 'failed';
    }
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    token += 1; // laufende Erzeugung gilt ab jetzt als überholt
    timer = setTimeout(() => {
      timer = null;
      generate();
    }, delay);
  }

  /** Abschnitt ist sichtbar geworden: erste Erzeugung sofort, danach folgen Änderungen mit Verzögerung. */
  function start() {
    if (started || disposed) return;
    started = true;
    generate();
    stopWatch = watch(
      () => keyOf(getPlayer()),
      (key) => {
        if (cache.has(key)) {
          if (timer) clearTimeout(timer);
          timer = null;
          token += 1;
          url.value = cache.get(key);
          status.value = 'ready';
        } else {
          schedule();
        }
      }
    );
  }

  /** Verlassen: Zeitgeber stoppen, Beobachter beenden, alle Blob-URLs freigeben. */
  function dispose() {
    disposed = true;
    if (timer) clearTimeout(timer);
    timer = null;
    token += 1;
    if (stopWatch) stopWatch();
    for (const u of cache.values()) rv(u);
    cache.clear();
    url.value = null;
  }

  return { url, status, start, dispose };
}
